import type { Locale } from '../i18n'
import type { Runtime } from '../types'
import type { RuntimeWebLaunchAvailability } from './webLaunch'
import { runtimeRelayLaunchAvailability } from './runtimeRelayLaunch'

export type RuntimeListFilter = 'all' | 'online' | 'attention' | 'offline'
export type RuntimeListSort = 'recent' | 'name' | 'status'
export type RuntimeHealthCode =
  | 'online'
  | 'starting'
  | 'recovering'
  | 'degraded'
  | 'needs_setup'
  | 'blocked'
  | 'offline'
  | 'unverifiable'

export interface RuntimeBadgePresentation<TCode extends string> {
  code: TCode
  label: string
  tone: 'success' | 'attention' | 'danger' | 'muted' | 'info'
}

const healthLabels: Record<RuntimeHealthCode, Record<Locale, string>> = {
  online: { 'zh-CN': '在线 · 就绪', 'en-US': 'Online · Ready' },
  starting: { 'zh-CN': '正在启动', 'en-US': 'Starting' },
  recovering: { 'zh-CN': '正在恢复', 'en-US': 'Recovering' },
  degraded: { 'zh-CN': '连接不稳定', 'en-US': 'Connection degraded' },
  needs_setup: { 'zh-CN': '需要设置', 'en-US': 'Setup required' },
  blocked: { 'zh-CN': '已阻塞', 'en-US': 'Blocked' },
  offline: { 'zh-CN': '离线', 'en-US': 'Offline' },
  unverifiable: { 'zh-CN': '状态不可验证', 'en-US': 'Status unverifiable' },
}

const webLabels: Record<RuntimeWebLaunchAvailability, Record<Locale, string>> = {
  available: { 'zh-CN': '可连接', 'en-US': 'Available' },
  offline: { 'zh-CN': '暂不可用', 'en-US': 'Unavailable' },
  starting: { 'zh-CN': '正在启动', 'en-US': 'Starting' },
  recovering: { 'zh-CN': '暂不可用', 'en-US': 'Unavailable' },
  degraded: { 'zh-CN': '暂不可用', 'en-US': 'Unavailable' },
  needs_setup: { 'zh-CN': '未启用', 'en-US': 'Not enabled' },
  blocked: { 'zh-CN': '暂不可用', 'en-US': 'Unavailable' },
  version_incompatible: { 'zh-CN': '版本不兼容', 'en-US': 'Version incompatible' },
  disabled: { 'zh-CN': '未启用', 'en-US': 'Not enabled' },
  expired: { 'zh-CN': '端点已过期', 'en-US': 'Endpoint expired' },
  unverifiable: { 'zh-CN': '状态不可验证', 'en-US': 'Status unverifiable' },
  unavailable: { 'zh-CN': '暂不可用', 'en-US': 'Unavailable' },
}

const webReasons: Record<Exclude<RuntimeWebLaunchAvailability, 'available'>, Record<Locale, string>> = {
  offline: { 'zh-CN': '运行环境当前离线，请在上线后刷新重试。', 'en-US': 'The Runtime is offline. Refresh after it comes back online.' },
  starting: { 'zh-CN': '运行环境正在启动，请稍后重试。', 'en-US': 'The Runtime is starting. Try again shortly.' },
  recovering: { 'zh-CN': '运行环境正在恢复，请等待状态稳定。', 'en-US': 'The Runtime is recovering. Wait until it is stable.' },
  degraded: { 'zh-CN': '连接状态不稳定，暂时无法安全打开 Web。', 'en-US': 'The connection is degraded, so Web cannot be opened safely.' },
  needs_setup: { 'zh-CN': '运行环境尚未完成 Web 访问设置。', 'en-US': 'Web access has not been configured for this Runtime.' },
  blocked: { 'zh-CN': '运行环境已阻塞，暂时无法连接。', 'en-US': 'The Runtime is blocked and cannot be connected.' },
  version_incompatible: { 'zh-CN': 'Runtime 协议版本不兼容，请升级后重试。', 'en-US': 'The Runtime protocol version is incompatible. Upgrade and retry.' },
  disabled: { 'zh-CN': '此 Runtime 未启用安全 Web 访问能力。', 'en-US': 'Secure Web access is not enabled for this Runtime.' },
  expired: { 'zh-CN': 'Web 端点已过期，请刷新运行环境状态。', 'en-US': 'The Web endpoint expired. Refresh the Runtime status.' },
  unverifiable: { 'zh-CN': '服务端暂时无法验证运行环境状态。', 'en-US': 'The server cannot currently verify the Runtime state.' },
  unavailable: { 'zh-CN': '安全 Web 连接尚未就绪，请稍后刷新重试。', 'en-US': 'The secure Web connection is not ready. Refresh and try again shortly.' },
}

export function runtimeHealthCode(runtime: Runtime): RuntimeHealthCode {
  if (runtime.presence === 'unknown') return 'unverifiable'
  if (runtime.presence === 'degraded') return 'degraded'
  if (runtime.presence !== 'present' || runtime.status === 'offline') return 'offline'
  if (runtime.readiness === 'unknown') return 'unverifiable'
  return runtime.readiness === 'ready' ? 'online' : runtime.readiness
}

export function runtimeHealthPresentation(runtime: Runtime, locale: Locale): RuntimeBadgePresentation<RuntimeHealthCode> {
  const code = runtimeHealthCode(runtime)
  const tone = code === 'online'
    ? 'success'
    : code === 'blocked'
      ? 'danger'
      : code === 'offline' || code === 'unverifiable'
        ? 'muted'
        : code === 'starting'
          ? 'info'
          : 'attention'
  return { code, label: healthLabels[code][locale], tone }
}

export function runtimeWebPresentation(runtime: Runtime, locale: Locale, _now = Date.now()): RuntimeBadgePresentation<RuntimeWebLaunchAvailability> & { reason: string } {
  const code = runtimeRelayLaunchAvailability(runtime)
  const tone = code === 'available'
    ? 'success'
    : code === 'starting'
      ? 'info'
      : ['offline', 'version_incompatible', 'disabled', 'unverifiable'].includes(code)
        ? 'muted'
        : 'attention'
  return {
    code,
    label: webLabels[code][locale],
    tone,
    reason: code === 'available'
      ? (locale === 'zh-CN' ? '可通过安全 Web 连接打开。' : 'Ready to open through a secure Web connection.')
      : webReasons[code][locale],
  }
}

export function runtimeFilterGroup(runtime: Runtime): Exclude<RuntimeListFilter, 'all'> {
  const code = runtimeHealthCode(runtime)
  if (code === 'online') return 'online'
  if (code === 'offline') return 'offline'
  return 'attention'
}

export function runtimeMatchesFilter(runtime: Runtime, filter: RuntimeListFilter): boolean {
  return filter === 'all' || runtimeFilterGroup(runtime) === filter
}

export function sortRuntimeList(items: Runtime[], sort: RuntimeListSort): Runtime[] {
  return [...items].sort((left, right) => {
    if (sort === 'name') return left.cloudDisplayName.localeCompare(right.cloudDisplayName)
    if (sort === 'status') {
      const order: RuntimeHealthCode[] = ['online', 'starting', 'recovering', 'degraded', 'needs_setup', 'blocked', 'unverifiable', 'offline']
      return order.indexOf(runtimeHealthCode(left)) - order.indexOf(runtimeHealthCode(right))
    }
    const leftTime = Date.parse(left.lastSeen)
    const rightTime = Date.parse(right.lastSeen)
    return (Number.isFinite(rightTime) ? rightTime : 0) - (Number.isFinite(leftTime) ? leftTime : 0)
  })
}

export function runtimeRelativeTime(value: string | undefined, locale: Locale, now = Date.now()): string {
  const time = value ? Date.parse(value) : Number.NaN
  if (!Number.isFinite(time)) return '—'
  const seconds = Math.round((time - now) / 1_000)
  if (Math.abs(seconds) < 45) return locale === 'zh-CN' ? '刚刚' : 'Just now'
  const minutes = Math.round(seconds / 60)
  if (Math.abs(minutes) < 60) return new Intl.RelativeTimeFormat(locale, { numeric: 'always' }).format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return new Intl.RelativeTimeFormat(locale, { numeric: 'always' }).format(hours, 'hour')
  const days = Math.round(hours / 24)
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(days, 'day')
}

export function compactRuntimeId(id: string): string {
  return id.length > 17 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id
}

export function shouldShowRuntimeRegion(items: Runtime[]): boolean {
  return items.length > 0 && items.filter((runtime) => Boolean(runtime.region)).length > items.length / 2
}
